// frontend/app/components/lessonPlans/teachsense/masteryBands.js
//
// The four-band mastery vocabulary, resolved in one place so every TeachSense
// section names and paints a band the same way.
//
// MASTERY_BAND_META is landing in lessonPlanTheme alongside this work. The
// namespace import plus a fallback means these render correctly whether or not
// it has arrived yet, and pick up the shared copy the moment it does; the
// fallback labels are the design document's own, so the two cannot disagree.

import * as lessonPlanTheme from "../lessonPlanTheme";

/** Low to high. This is also the order a distribution bar stacks in. */
export const BAND_KEYS = ["discovering", "building", "applying", "extending"];

const FALLBACK_BAND_META = {
  discovering: { label: "Discovering" },
  building: { label: "Building" },
  applying: { label: "Applying" },
  extending: { label: "Extending" },
};

// A band's accent is a semantic token, so it is dark-mode correct; the fixed
// hex beside it is only what tint() needs to wash a background from. Same split
// LessonInsightsPanel's bandFor() uses, so a band pill and the bar it sits on
// are never two different ambers.
const BAND_TONE = {
  discovering: { token: "danger", hex: "#ef4444" },
  building: { token: "warning", hex: "#f59e0b" },
  applying: { token: "info", hex: "#3b82f6" },
  extending: { token: "success", hex: "#10b981" },
};

const UNKNOWN_TONE = { token: "textMuted", hex: "#64748b" };

const metaFor = (key) => {
  if (typeof lessonPlanTheme.getMasteryBandMeta === "function") {
    return lessonPlanTheme.getMasteryBandMeta(key);
  }
  const shared = lessonPlanTheme.MASTERY_BAND_META || null;
  return (shared && shared[key]) || FALLBACK_BAND_META[key] || null;
};

/**
 * Band boundaries from the design document: <0.2 Discovering, <0.6 Building,
 * <0.9 Applying, >= 0.9 Extending. The server sends `band` already — this is
 * only what a payload missing it falls back to, so a bar can never be drawn in
 * a colour that contradicts its own number.
 */
export const bandForScore = (mastery) => {
  const m = Number(mastery);
  // null and "" both coerce to 0, which would band an unmeasured concept as
  // Discovering — the one reading this whole section exists to prevent.
  if (mastery === null || mastery === undefined || mastery === "" || !Number.isFinite(m)) {
    return null;
  }
  if (m < 0.2) return "discovering";
  if (m < 0.6) return "building";
  if (m < 0.9) return "applying";
  return "extending";
};

/**
 * `m` is a [0,1] score, not a percentage — 0.62 is 62%. Anything outside the
 * range is clamped rather than drawn as a fill wider than its own track, and
 * anything unparseable comes back null so the caller can draw "no evidence"
 * instead of a zero.
 */
export const masteryPercent = (mastery) => {
  const m = Number(mastery);
  if (mastery === null || mastery === undefined || mastery === "" || !Number.isFinite(m)) {
    return null;
  }
  return Math.round(Math.min(1, Math.max(0, m)) * 100);
};

/** `{ key, label, accent, hex }` — everything a band needs in order to be drawn. */
export const bandVisual = (key, colors, t) => {
  const translate = t || ((value) => value);
  const normalized = String(key || "").toLowerCase();
  const tone = BAND_TONE[normalized] || UNKNOWN_TONE;
  const meta = metaFor(normalized);
  // The shared meta's accent wins wherever it exists, so "Applying" is one
  // colour on the teacher's bar and on the student's own mastery map rather
  // than two. The semantic token is what a band the theme has never heard of
  // falls back to — and it is the only one of the two that is dark-aware, so
  // the shared palette has to stay legible on both grounds by itself.
  const shared = typeof meta?.accent === "string" ? meta.accent : null;
  return {
    key: normalized,
    label: translate(meta?.label || "Unknown"),
    accent: shared || colors[tone.token],
    hex: shared || tone.hex,
  };
};
